/**
 * Admin Anomaly Policy Configuration Page
 * Interface for configuring anomaly detection policies
 * Implements Requirements: 2.1, 2.2 - Policy configuration with JSON validation
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui';
import { adminAnomalyService, PolicyConfig, ValidationResult } from '../services/admin-anomaly.service';

const AdminAnomalyPolicyPage: React.FC = () => {
  const navigate = useNavigate();
  const [config, setConfig] = useState<PolicyConfig>(adminAnomalyService.getDefaultPolicyConfig());
  const [originalConfig, setOriginalConfig] = useState<PolicyConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showJsonEditor, setShowJsonEditor] = useState(false);
  const [jsonText, setJsonText] = useState('');
  const [needsPersist, setNeedsPersist] = useState(false);

  useEffect(() => {
    loadCurrentPolicy();
  }, []);

  const loadCurrentPolicy = async () => {
    try {
      setLoading(true);
      setError(null);

      const policies = await adminAnomalyService.getPolicies('global', 'global');
      
      if (policies.global) {
        setConfig(policies.global.config);
        setOriginalConfig(policies.global.config);
        setJsonText(JSON.stringify(policies.global.config, null, 2));
        setNeedsPersist(false);
      } else {
        // No policy exists yet: seed with defaults and persist once
        const defaultConfig = adminAnomalyService.getDefaultPolicyConfig();
        setConfig(defaultConfig);
        setOriginalConfig(defaultConfig);
        setJsonText(JSON.stringify(defaultConfig, null, 2));
        setNeedsPersist(true);
        try {
          await adminAnomalyService.updatePolicy('global', 'global', defaultConfig);
          setSuccess('Default policy created and saved.');
          setNeedsPersist(false);
        } catch (saveErr: any) {
          console.error('Failed to save default policy:', saveErr);
          setError(
            saveErr.response?.data?.error ||
              saveErr.response?.data?.message ||
              'Default policy loaded, but failed to save to server. Try saving manually.'
          );
        }
      }
    } catch (err: any) {
      console.error('Failed to load policy:', err);
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to load current policy');
    } finally {
      setLoading(false);
    }
  };

  const validateConfig = async (configToValidate: PolicyConfig) => {
    try {
      setValidating(true);
      const result = await adminAnomalyService.validatePolicy(configToValidate);
      setValidation(result);
      return result.isValid;
    } catch (err: any) {
      console.error('Validation failed:', err);
      setValidation({
        isValid: false,
        errors: [err.response?.data?.message || 'Validation failed']
      });
      return false;
    } finally {
      setValidating(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      // Validate first
      const isValid = await validateConfig(config);
      if (!isValid) {
        setError('Configuration is invalid. Please fix the errors before saving.');
        return;
      }

      // Save policy
      await adminAnomalyService.updatePolicy('global', 'global', config);
      
      setOriginalConfig(config);
      setNeedsPersist(false);
      setSuccess('Policy updated successfully. Changes are now active.');
    } catch (err: any) {
      console.error('Failed to save policy:', err);
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to save policy');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (originalConfig) {
      setConfig(originalConfig);
      setJsonText(JSON.stringify(originalConfig, null, 2));
      setValidation(null);
      setError(null);
      setSuccess(null);
    }
  };

  const handleJsonUpdate = () => {
    try {
      const parsedConfig = JSON.parse(jsonText);
      setConfig(parsedConfig);
      setError(null);
      validateConfig(parsedConfig);
    } catch (err) {
      setError('Invalid JSON format');
    }
  };

  const updateConfigValue = (path: string[], value: any) => {
    const newConfig = { ...config };
    let current: any = newConfig;
    
    for (let i = 0; i < path.length - 1; i++) {
      current = current[path[i]];
    }
    
    current[path[path.length - 1]] = value;
    setConfig(newConfig);
    setJsonText(JSON.stringify(newConfig, null, 2));
    
    // Auto-validate on change
    validateConfig(newConfig);
  };

  const hasChanges =
    needsPersist || (originalConfig ? JSON.stringify(config) !== JSON.stringify(originalConfig) : false);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading policy configuration...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-gray-900">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Anomaly Detection Policy</h1>
            <p className="text-sm text-gray-600 mt-1">
              Configure risk assessment parameters and thresholds
            </p>
          </div>
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate('/admin')}>
              Back to Admin
            </Button>
          </div>
        </div>

        {/* Status Messages */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex">
              <svg className="w-5 h-5 text-red-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-red-800">{error}</p>
            </div>
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex">
              <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <p className="text-green-800">{success}</p>
            </div>
          </div>
        )}

        {/* Validation Results */}
        {validation && !validation.isValid && (
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <h3 className="text-yellow-800 font-medium mb-2">Configuration Errors:</h3>
            <ul className="list-disc list-inside text-yellow-700">
              {validation.errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        {validation && validation.isValid && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex">
              <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <p className="text-green-800">Configuration is valid</p>
            </div>
          </div>
        )}

        {/* Editor Toggle */}
        <div className="mb-6 bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900">Configuration Editor</h2>
            <div className="flex gap-2">
              <Button
                variant={!showJsonEditor ? ("primary" as const) : ("outline" as const)}
                onClick={() => setShowJsonEditor(false)}
              >
                Form Editor
              </Button>
              <Button
                variant={showJsonEditor ? ("primary" as const) : ("outline" as const)}
                onClick={() => setShowJsonEditor(true)}
              >
                JSON Editor
              </Button>
            </div>
          </div>
        </div>

        {/* Form Editor */}
        {!showJsonEditor && (
          <div className="space-y-6">
            {/* Burst Detection Settings */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Burst Detection</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Product Scans per Hour
                  </label>
                  <input
                    type="number"
                    value={config.burst.product_scans_per_hour}
                    onChange={(e) => updateConfigValue(['burst', 'product_scans_per_hour'], parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Product Scans per Day
                  </label>
                  <input
                    type="number"
                    value={config.burst.product_scans_per_day}
                    onChange={(e) => updateConfigValue(['burst', 'product_scans_per_day'], parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    IP Scans per Minute
                  </label>
                  <input
                    type="number"
                    value={config.burst.ip_scans_per_minute}
                    onChange={(e) => updateConfigValue(['burst', 'ip_scans_per_minute'], parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Distinct Products per IP Hour
                  </label>
                  <input
                    type="number"
                    value={config.burst.distinct_products_per_ip_hour}
                    onChange={(e) => updateConfigValue(['burst', 'distinct_products_per_ip_hour'], parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Grace Period (seconds)
                  </label>
                  <input
                    type="number"
                    value={config.burst.ignore_repeat_seconds}
                    onChange={(e) => updateConfigValue(['burst', 'ignore_repeat_seconds'], parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Geographic Settings */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Geographic Detection</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={config.geo.enabled}
                      onChange={(e) => updateConfigValue(['geo', 'enabled'], e.target.checked)}
                      className="mr-2"
                      aria-label="Enable geographic anomaly detection"
                    />
                    <span className="text-sm font-medium text-gray-700">Enable Geographic Detection</span>
                  </label>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Country Jump Window (minutes)
                  </label>
                  <input
                    type="number"
                    value={config.geo.country_jump_minutes}
                    onChange={(e) => updateConfigValue(['geo', 'country_jump_minutes'], parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={!config.geo.enabled}
                  />
                </div>
              </div>
            </div>

            {/* Network Settings */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Network Detection</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Datacenter Weight (0-100)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={config.network.datacenter_weight}
                    onChange={(e) => updateConfigValue(['network', 'datacenter_weight'], parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    VPN Weight (0-100)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={config.network.vpn_weight}
                    onChange={(e) => updateConfigValue(['network', 'vpn_weight'], parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Risk Weights */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Risk Signal Weights</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {Object.entries(config.weights).map(([key, value]) => (
                  <div key={key}>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {key.replace('_', ' ')} (0-100)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={value}
                      onChange={(e) => updateConfigValue(['weights', key], parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Decision Thresholds */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Decision Thresholds</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Medium Risk Threshold (0-100)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={config.decision.medium}
                    onChange={(e) => updateConfigValue(['decision', 'medium'], parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    High Risk Threshold (0-100)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={config.decision.high}
                    onChange={(e) => updateConfigValue(['decision', 'high'], parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={config.decision.high_requires_multiple_signals}
                      onChange={(e) => updateConfigValue(['decision', 'high_requires_multiple_signals'], e.target.checked)}
                      className="mr-2"
                      aria-label="Require multiple signals for high risk decisions"
                    />
                    <span className="text-sm font-medium text-gray-700">High Risk Requires Multiple Signals</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* JSON Editor */}
        {showJsonEditor && (
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">JSON Configuration</h3>
            <textarea
              value={jsonText}
              onChange={(e) => setJsonText(e.target.value)}
              className="w-full h-96 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
              placeholder="Enter JSON configuration..."
            />
            <div className="mt-4">
              <Button onClick={handleJsonUpdate} disabled={validating}>
                {validating ? 'Validating...' : 'Update from JSON'}
              </Button>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              {hasChanges ? 'You have unsaved changes' : 'No changes to save'}
            </div>
            <div className="flex gap-4">
              <Button
                variant="outline"
                onClick={handleReset}
                disabled={!hasChanges || saving}
              >
                Reset Changes
              </Button>
              <Button
                onClick={handleSave}
                disabled={!hasChanges || saving || (validation ? !validation.isValid : false)}
              >
                {saving ? 'Saving...' : 'Save Policy'}
              </Button>
            </div>
          </div>
        </div>
    </div>
  );
};

export default AdminAnomalyPolicyPage;
